'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface APIEndpoint {
  method: string;
  path: string;
  description: string;
  category: string;
}

interface CodeExample {
  language: string;
  code: string;
}

export default function APIExplorer() {
  const [selectedCategory, setSelectedCategory] = useState('PNR Management');
  const [selectedEndpoint, setSelectedEndpoint] = useState(0);
  const [selectedLanguage, setSelectedLanguage] = useState('JavaScript');

  const categories = ['PNR Management', 'Booking', 'Payments', 'Analytics'];

  const endpoints: APIEndpoint[] = [
    {
      method: 'POST',
      path: '/api/v1/pnr/create',
      description: 'Create a new PNR with real-time airline confirmation',
      category: 'PNR Management'
    },
    {
      method: 'GET',
      path: '/api/v1/pnr/{pnr_code}',
      description: 'Retrieve PNR details and current status',
      category: 'PNR Management'
    },
    {
      method: 'PUT',
      path: '/api/v1/pnr/{pnr_code}/update',
      description: 'Update passenger or flight information',
      category: 'PNR Management'
    },
    {
      method: 'POST',
      path: '/api/v1/booking/search',
      description: 'Search available flights with real-time pricing',
      category: 'Booking'
    },
    {
      method: 'POST',
      path: '/api/v1/booking/hold',
      description: 'Place a temporary hold on selected flights',
      category: 'Booking'
    },
    {
      method: 'POST',
      path: '/api/v1/payment/process',
      description: 'Process payment and confirm booking',
      category: 'Payments'
    },
    {
      method: 'GET',
      path: '/api/v1/analytics/revenue',
      description: 'Get revenue analytics and booking statistics',
      category: 'Analytics'
    }
  ];

  const codeExamples: Record<string, CodeExample[]> = {
    'JavaScript': [
      {
        language: 'JavaScript',
        code: `// Initialize Dialforflight SDK
import TravelGateway from '@travelgateway/sdk';

const gateway = new TravelGateway({
  apiKey: process.env.TRAVELGATEWAY_API_KEY,
  environment: 'production'
});

// Create PNR
const createPNR = async () => {
  try {
    const booking = await gateway.pnr.create({
      passengers: [
        {
          firstName: 'John',
          lastName: 'Doe',
          dateOfBirth: '1985-06-15',
          passportNumber: 'AB1234567'
        }
      ],
      flights: [
        {
          origin: 'JFK',
          destination: 'LHR',
          departureDate: '2026-03-15',
          flightNumber: 'BA178'
        }
      ],
      contactInfo: {
        email: 'john.doe@example.com',
        phone: '+1-555-0123'
      }
    });

    console.log('PNR Created:', booking.pnr);
    console.log('Status:', booking.status);
    console.log('Confirmation:', booking.airlineConfirmation);
    
    return booking;
  } catch (error) {
    console.error('PNR Creation Failed:', error);
  }
};`
      }
    ],
    'Python': [
      {
        language: 'Python',
        code: `# Initialize Dialforflight SDK
from dialforflight import Dialforflight

gateway = Dialforflight(
    api_key=os.environ.get('TRAVELGATEWAY_API_KEY'),
    environment='production'
)

# Create PNR
def create_pnr():
    try:
        booking = gateway.pnr.create(
            passengers=[
                {
                    'first_name': 'John',
                    'last_name': 'Doe',
                    'date_of_birth': '1985-06-15',
                    'passport_number': 'AB1234567'
                }
            ],
            flights=[
                {
                    'origin': 'JFK',
                    'destination': 'LHR',
                    'departure_date': '2026-03-15',
                    'flight_number': 'BA178'
                }
            ],
            contact_info={
                'email': 'john.doe@example.com',
                'phone': '+1-555-0123'
            }
        )

        print(f"PNR Created: {booking['pnr']}")
        print(f"Status: {booking['status']}")
        print(f"Confirmation: {booking['airline_confirmation']}")
        
        return booking
    except Exception as error:
        print(f"PNR Creation Failed: {error}")`
      }
    ],
    'cURL': [
      {
        language: 'cURL',
        code: `# Create PNR using cURL
curl -X POST https://api.travelgateway.com/v1/pnr/create \\
  -H "Authorization: Bearer YOUR_API_KEY" \ -H"Content-Type: application/json" \\
  -d '{
    "passengers": [
      {
        "firstName": "John",
        "lastName": "Doe",
        "dateOfBirth": "1985-06-15",
        "passportNumber": "AB1234567"
      }
    ],
    "flights": [
      {
        "origin": "JFK",
        "destination": "LHR",
        "departureDate": "2026-03-15",
        "flightNumber": "BA178"
      }
    ],
    "contactInfo": {
      "email": "john.doe@example.com",
      "phone": "+1-555-0123"
    }
  }'

# Response (200 OK)
{
  "pnr": "TG7X9K2M",
  "status": "CONFIRMED",
  "airlineConfirmation": "BA-2026-031578",
  "createdAt": "2026-01-30T10:56:19Z"
}`
      }
    ]
  };

  const filteredEndpoints = endpoints.filter(e => e.category === selectedCategory);
  const currentEndpoint = filteredEndpoints[selectedEndpoint];

  const getMethodColor = (method: string) => {
    switch (method) {
      case 'GET': return 'bg-sky-blue/10 text-sky-blue';
      case 'POST': return 'bg-success/10 text-success';
      case 'PUT': return 'bg-warning/10 text-warning';
      case 'DELETE': return 'bg-error/10 text-error';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <section className="py-16 lg:py-24 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center space-x-2 bg-primary/10 px-4 py-2 rounded-full mb-4">
            <Icon name="CodeBracketIcon" size={20} className="text-primary" />
            <span className="text-sm font-medium text-primary">Interactive API Explorer</span>
          </div>
          <h2 className="font-heading text-3xl lg:text-4xl font-bold text-foreground mb-4">
            Test Our APIs in Real-Time
          </h2>
          <p className="text-lg text-muted-foreground">
            Explore our comprehensive API documentation with live code examples. Copy, modify, and test endpoints directly in your browser.
          </p>
        </div>

        {/* API Explorer Interface */}
        <div className="bg-card rounded-2xl shadow-brand-lg overflow-hidden border border-border">
          <div className="grid lg:grid-cols-12">
            {/* Left Sidebar - Endpoints */}
            <div className="lg:col-span-4 border-r border-border bg-surface">
              {/* Category Tabs */}
              <div className="border-b border-border p-4">
                <div className="space-y-2">
                  {categories.map((category) => (
                    <button
                      key={category}
                      onClick={() => {
                        setSelectedCategory(category);
                        setSelectedEndpoint(0);
                      }}
                      className={`w-full text-left px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                        selectedCategory === category
                          ? 'bg-primary text-white' :'text-foreground hover:bg-muted'
                      }`}
                    >
                      {category}
                    </button>
                  ))}
                </div>
              </div>

              {/* Endpoints List */}
              <div className="p-4 space-y-2 max-h-96 overflow-y-auto">
                {filteredEndpoints.map((endpoint, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedEndpoint(index)}
                    className={`w-full text-left p-3 rounded-lg transition-colors duration-200 ${
                      selectedEndpoint === index
                        ? 'bg-card shadow-sm border border-primary'
                        : 'hover:bg-muted'
                    }`}
                  >
                    <div className="flex items-center space-x-2 mb-1">
                      <span className={`px-2 py-0.5 rounded text-xs font-mono font-semibold ${getMethodColor(endpoint.method)}`}>
                        {endpoint.method}
                      </span>
                    </div>
                    <div className="font-mono text-xs text-muted-foreground mb-1">
                      {endpoint.path}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {endpoint.description}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Right Content - Code Examples */}
            <div className="lg:col-span-8">
              {/* Language Selector */}
              <div className="border-b border-border p-4 bg-charcoal">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <span className={`px-3 py-1 rounded text-sm font-mono font-semibold ${getMethodColor(currentEndpoint.method)}`}>
                      {currentEndpoint.method}
                    </span>
                    <span className="font-mono text-sm text-white">
                      {currentEndpoint.path}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    {Object.keys(codeExamples).map((lang) => (
                      <button
                        key={lang}
                        onClick={() => setSelectedLanguage(lang)}
                        className={`px-3 py-1 rounded text-sm font-medium transition-colors duration-200 ${
                          selectedLanguage === lang
                            ? 'bg-primary text-white' :'text-white/60 hover:text-white hover:bg-white/10'
                        }`}
                      >
                        {lang}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Code Display */}
              <div className="bg-[#1e1e1e] p-6 font-mono text-sm overflow-x-auto max-h-96 overflow-y-auto">
                <pre className="text-white/90 leading-relaxed">
                  <code>{codeExamples[selectedLanguage][0].code}</code>
                </pre>
              </div>

              {/* Action Buttons */}
              <div className="border-t border-border p-4 bg-surface flex items-center justify-between">
                <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                  <Icon name="InformationCircleIcon" size={18} />
                  <span>Replace YOUR_API_KEY with your actual API key</span>
                </div>
                <button className="inline-flex items-center space-x-2 bg-primary hover:bg-secondary text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200">
                  <Icon name="ClipboardDocumentIcon" size={18} />
                  <span>Copy Code</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Links */}
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          <div className="bg-card p-6 rounded-xl shadow-brand hover:shadow-brand-lg transition-shadow duration-300">
            <Icon name="BookOpenIcon" size={32} className="text-primary mb-4" />
            <h3 className="font-heading text-lg font-semibold text-foreground mb-2">
              Full Documentation
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Comprehensive guides, tutorials, and API reference documentation.
            </p>
            <button className="text-primary hover:text-secondary font-medium text-sm inline-flex items-center space-x-1">
              <span>Read Docs</span>
              <Icon name="ArrowRightIcon" size={16} />
            </button>
          </div>

          <div className="bg-card p-6 rounded-xl shadow-brand hover:shadow-brand-lg transition-shadow duration-300">
            <Icon name="BeakerIcon" size={32} className="text-primary mb-4" />
            <h3 className="font-heading text-lg font-semibold text-foreground mb-2">
              Sandbox Environment
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Test all endpoints safely without affecting production data.
            </p>
            <button className="text-primary hover:text-secondary font-medium text-sm inline-flex items-center space-x-1">
              <span>Access Sandbox</span>
              <Icon name="ArrowRightIcon" size={16} />
            </button>
          </div>

          <div className="bg-card p-6 rounded-xl shadow-brand hover:shadow-brand-lg transition-shadow duration-300">
            <Icon name="ChatBubbleLeftRightIcon" size={32} className="text-primary mb-4" />
            <h3 className="font-heading text-lg font-semibold text-foreground mb-2">
              Developer Community
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Join our community forum to connect with other developers.
            </p>
            <button className="text-primary hover:text-secondary font-medium text-sm inline-flex items-center space-x-1">
              <span>Join Community</span>
              <Icon name="ArrowRightIcon" size={16} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}